import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from "html5-qrcode";
import { X, ScanLine, Keyboard } from "lucide-react";

const FORMATS = [
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.CODE_93,
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.ITF,
  Html5QrcodeSupportedFormats.CODABAR,
  Html5QrcodeSupportedFormats.QR_CODE,
];

/**
 * Full-screen camera barcode scanner. Calls onDetected(text). Consistent in-app
 * camera (no external app). Falls back to manual typing.
 *
 * Start is deferred one macrotask so React StrictMode's throwaway mount never
 * actually opens the camera (which would leak a pending video.play() rejection).
 * Close always fully stops the camera before unmounting.
 */
export default function BarcodeScanner({ title = "Scan barcode", onDetected, onClose, continuous = false }) {
  const regionId = useRef(`bc-region-${Math.random().toString(36).slice(2)}`);
  const scannerRef = useRef(null);
  const startPromiseRef = useRef(null);
  const startTimerRef = useRef(null);
  const aliveRef = useRef(true);
  const stoppingRef = useRef(false);
  const doneRef = useRef(false);
  const lastRef = useRef({ code: "", at: 0 });
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");

  const stopScanner = async () => {
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    aliveRef.current = false;
    if (startTimerRef.current) { clearTimeout(startTimerRef.current); startTimerRef.current = null; }
    try { await startPromiseRef.current; } catch { /* start failed */ }
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.getState && s.getState() === Html5QrcodeScannerState.SCANNING) await s.stop();
    } catch { /* already stopped */ }
    try { await s.clear(); } catch { /* noop */ }
  };

  useEffect(() => {
    aliveRef.current = true;
    stoppingRef.current = false;
    doneRef.current = false;
    const onScan = (decodedText) => {
      const code = String(decodedText).trim();
      if (continuous) {
        const now = Date.now();
        if (lastRef.current.code === code && now - lastRef.current.at < 1500) return;
        lastRef.current = { code, at: now };
        onDetected(code);
        return;
      }
      if (doneRef.current) return;
      doneRef.current = true;
      stopScanner().finally(() => onDetected(code));
    };
    startTimerRef.current = setTimeout(() => {
      if (!aliveRef.current) return;
      const scanner = new Html5Qrcode(regionId.current, { formatsToSupport: FORMATS, verbose: false });
      scannerRef.current = scanner;
      startPromiseRef.current = scanner
        .start({ facingMode: "environment" }, { fps: 12, qrbox: { width: 300, height: 200 }, aspectRatio: 1.4 }, onScan, () => {})
        .catch(() => { if (aliveRef.current) setError("Camera unavailable — type the number instead."); });
    }, 80);
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  const submitManual = async () => {
    const v = manual.trim();
    if (!v) return;
    doneRef.current = true;
    if (!continuous) await stopScanner();
    onDetected(v);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/95 flex flex-col" data-testid="barcode-scanner">
      <div className="flex items-center justify-between px-6 py-4 text-white">
        <span className="text-lg font-semibold inline-flex items-center gap-2"><ScanLine className="w-6 h-6 text-orange-400" /> {title}</span>
        <button onClick={handleClose} data-testid="barcode-close-btn" className="p-3 rounded-full hover:bg-white/10"><X className="w-7 h-7" /></button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 gap-4">
        {error ? (
          <p className="text-white/80 text-center max-w-sm">{error}</p>
        ) : (
          <>
            <div id={regionId.current} className="w-full max-w-md rounded-2xl overflow-hidden bg-black" />
            <p className="text-white/60 text-sm">Point the camera at the order or tracking barcode.</p>
          </>
        )}

        <div className="w-full max-w-md mt-2">
          <label className="text-white/50 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1"><Keyboard className="w-3.5 h-3.5" /> Or type it in</label>
          <div className="flex gap-2">
            <input
              data-testid="barcode-manual-input"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitManual()}
              placeholder="Enter number…"
              className="flex-1 text-lg font-mono px-4 py-3 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-orange-400/40"
            />
            <button onClick={submitManual} data-testid="barcode-manual-submit" className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 rounded-xl">Use</button>
          </div>
        </div>
      </div>
    </div>
  );
}
